import React, { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const STATUSES = ["active", "pending_review", "draft", "under_contract", "pending_offer", "sold", "withdrawn"];

export default function ListingsTab() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [listed, setListed] = useState("");
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState(null); // { note_id, events }

  const load = () => {
    setLoading(true);
    const params = { limit: 300 };
    if (q) params.q = q;
    if (status) params.status = status;
    if (listed) params.listed = listed;
    api.get("/admin/listings", { params })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, [status, listed]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(load, 400); return () => clearTimeout(t); }, [q]);

  const act = async (note_id, action) => {
    if (action === "delete" && !window.confirm("Permanently delete this listing? Deals referencing it must be closed first.")) return;
    try {
      if (action === "delete") await api.delete(`/admin/listings/${note_id}`);
      else await api.post(`/admin/listings/${note_id}/${action}`);
      toast.success(`Listing ${action}d`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const feature = async (note_id) => {
    try {
      await api.post(`/marketplace/featured`, { note_id });
      toast.success("Featured slot reserved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const showHistory = async (note_id) => {
    try {
      const r = await api.get(`/admin/listings/${note_id}/history`);
      setHistory({ note_id, events: r.data.events || [] });
    } catch { setHistory({ note_id, events: [] }); }
  };

  return (
    <div className="space-y-4" data-testid="admin-listings-tab">
      <div className="flex flex-wrap items-center gap-2 border border-border p-3">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search vehicle, state, note id…" className="rounded-none max-w-xs" data-testid="listings-search" />
        <Select value={status} onValueChange={(v) => setStatus(v === "__all__" ? "" : v)}>
          <SelectTrigger className="rounded-none w-48" data-testid="listings-status-filter"><SelectValue placeholder="Any status" /></SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="__all__">Any status</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={listed} onValueChange={(v) => setListed(v === "__all__" ? "" : v)}>
          <SelectTrigger className="rounded-none w-40" data-testid="listings-listed-filter"><SelectValue placeholder="Visibility" /></SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="__all__">Any visibility</SelectItem>
            <SelectItem value="true">Listed</SelectItem>
            <SelectItem value="false">Hidden</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" className="rounded-none uppercase text-xs h-9 ml-auto" onClick={load} data-testid="listings-refresh">Refresh</Button>
      </div>

      <div className="border border-border overflow-x-auto" data-testid="listings-table">
        <table className="w-full text-sm min-w-[1000px]">
          <thead className="bg-muted/50 text-left">
            <tr>
              <Th>Vehicle</Th><Th>Owner</Th><Th>Status</Th><Th>Listed</Th><Th>Featured</Th><Th>Asking</Th><Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No listings.</td></tr>}
            {items.map((n) => (
              <tr key={n.note_id} className="border-t border-border" data-testid={`listings-row-${n.note_id}`}>
                <Td>
                  <Link to={`/notes/${n.note_id}`} className="text-primary hover:underline text-[12px]">
                    {n.vehicle_year} {n.vehicle_make} {n.vehicle_model}
                  </Link>
                  <div className="text-[10px] text-muted-foreground font-mono">{n.state} · {n.note_id.slice(-8)}</div>
                </Td>
                <Td className="text-[11px]">{n.seller?.email || n.seller_id?.slice(-8) || "—"}</Td>
                <Td><span className="text-[10px] font-mono uppercase">{n.listing_status || "—"}</span></Td>
                <Td><span className="text-[10px] font-mono uppercase">{n.listed ? "Yes" : "No"}</span></Td>
                <Td><span className="text-[10px] font-mono uppercase">{n.featured_until ? "Yes" : "No"}</span></Td>
                <Td className="font-mono-data text-[12px]">{fmtMoney(n.asking_price)}</Td>
                <Td>
                  <div className="flex gap-1 flex-wrap">
                    {n.listing_status === "pending_review" && (
                      <Btn onClick={() => act(n.note_id, "activate")} testid={`listings-approve-${n.note_id}`} tone="primary">Approve</Btn>
                    )}
                    {n.listing_status !== "pending_review" && (
                      n.listed ? (
                        <Btn onClick={() => act(n.note_id, "hide")} testid={`listings-hide-${n.note_id}`}>Hide</Btn>
                      ) : (
                        <Btn onClick={() => act(n.note_id, "activate")} testid={`listings-activate-${n.note_id}`}>Activate</Btn>
                      )
                    )}
                    <Btn onClick={() => act(n.note_id, "archive")} testid={`listings-archive-${n.note_id}`}>Archive</Btn>
                    <Btn onClick={() => feature(n.note_id)} testid={`listings-feature-${n.note_id}`}>Feature</Btn>
                    <Btn onClick={() => showHistory(n.note_id)} testid={`listings-history-${n.note_id}`}>History</Btn>
                    <Btn onClick={() => act(n.note_id, "delete")} testid={`listings-delete-${n.note_id}`} tone="danger">Delete</Btn>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {history && (
        <Dialog open onOpenChange={() => setHistory(null)}>
          <DialogContent className="rounded-none max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="listing-history-dialog">
            <DialogHeader><DialogTitle>Listing History · {history.note_id.slice(-8)}</DialogTitle></DialogHeader>
            <div className="text-xs font-mono max-h-96 overflow-y-auto">
              {history.events.length === 0 && <div className="text-muted-foreground p-3">No history yet.</div>}
              {history.events.map((e, i) => (
                <div key={i} className="flex gap-3 border-b border-border/50 py-1">
                  <span className="text-muted-foreground whitespace-nowrap">{e.at ? new Date(e.at).toLocaleString() : ""}</span>
                  <span className="uppercase text-primary">{e.event}</span>
                  <span className="text-muted-foreground ml-auto">{e.actor_role || ""} {(e.actor_id || "").slice(-6)}</span>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-none" onClick={() => setHistory(null)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

const Th = ({ children }) => <th className="px-3 py-2 font-overline text-muted-foreground text-[10px] whitespace-nowrap">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-3 py-2 ${className}`}>{children}</td>;
const Btn = ({ children, onClick, testid, tone }) => (
  <Button size="sm" variant={tone === "primary" ? "default" : "outline"}
          className={`rounded-none text-[10px] h-7 uppercase ${tone === "danger" ? "text-red-600" : ""}`}
          onClick={onClick} data-testid={testid}>
    {children}
  </Button>
);
